import React, { useState } from 'react';
import { 
  CheckCircle, 
  Calendar, 
  Clock, 
  MapPin 
} from 'lucide-react';

const STATUS_OPTIONS = ['cancelado', 'pendiente', 'entregado', 'rechazado'];
const SLOT_OPTIONS = [
  { label: '1–4 pm', value: 1 },
  { label: '2–5 pm', value: 2 },
  { label: '3–6 pm', value: 3 },
  { label: '4–7 pm', value: 4 },
  { label: '5–8 pm', value: 5 },
];

async function patchDeliveryPoint(dpId, payload) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${process.env.REACT_APP_API_URL}/api/delivery_points/${dpId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`PATCH failed: ${res.status}`);
  return res.json();
}

function ChatViewHelpers({ deliveryPointID }) {
  const [statusOpen, setStatusOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [slotOpen, setSlotOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [dateISO, setDateISO] = useState('');
  const [latLngStr, setLatLngStr] = useState('');

  if (!deliveryPointID) return null;

  async function handleStatusChange(status) {
    try {
      await patchDeliveryPoint(deliveryPointID, { status });
      setStatusOpen(false);
      alert(`Estado actualizado a: ${status}`);
    } catch (e) {
      console.error(e);
      alert('No se pudo actualizar el estado.');
    }
  }

  async function handleDateSave() {
    if (!dateISO) return;
    try {
      await patchDeliveryPoint(deliveryPointID, { delivery_date: dateISO });
      setDateOpen(false);
      setDateISO('');
      alert('Fecha actualizada correctamente');
    } catch (e) {
      console.error(e);
      alert('No se pudo actualizar la fecha.');
    }
  }

  async function handleSlotChange(slot) {
    try {
      await patchDeliveryPoint(deliveryPointID, { slot });
      setSlotOpen(false);
      alert(`Horario actualizado a: ${SLOT_OPTIONS.find(s => s.value === slot)?.label}`);
    } catch (e) {
      console.error(e);
      alert('No se pudo actualizar el horario.');
    }
  }

  async function handleLocationSave() {
    const parts = latLngStr.split(',').map(s => s.trim());
    if (parts.length !== 2) {
      alert('Formato inválido. Use: -12.042104, -77.06698');
      return;
    }
    const lat = Number(parts[0]);
    const lng = Number(parts[1]);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      alert('Lat/Lng inválidas.');
      return;
    }
    try {
      await patchDeliveryPoint(deliveryPointID, { latitude: lat, longitude: lng });
      setLocOpen(false);
      setLatLngStr('');
      alert('Ubicación guardada correctamente');
    } catch (e) {
      console.error(e);
      alert('No se pudo guardar la ubicación.');
    }
  }

  return (
    <div className="flex items-center gap-2">
      {/* Status */}
      <div className="relative">
        <button
          onClick={() => setStatusOpen(!statusOpen)}
          className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-brand-primary"
          title="Cambiar estado"
        >
          <CheckCircle className="w-5 h-5" />
        </button>
        {statusOpen && (
          <div className="absolute top-full right-0 mt-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 min-w-[140px]">
            {STATUS_OPTIONS.map(status => (
              <button
                key={status}
                onClick={() => handleStatusChange(status)}
                className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg font-montserrat"
              >
                {status}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Date */}
      <div className="relative">
        <button
          onClick={() => setDateOpen(!dateOpen)}
          className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-brand-gold"
          title="Cambiar fecha"
        >
          <Calendar className="w-5 h-5" />
        </button>
        {dateOpen && (
          <div className="absolute top-full right-0 mt-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 p-4 w-64">
            <p className="text-sm font-semibold text-white mb-2 font-montserrat">
              Selecciona fecha
            </p>
            <input
              type="date"
              value={dateISO}
              onChange={e => setDateISO(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded-lg outline-none focus:border-brand-primary mb-3 font-montserrat"
            />
            <button
              onClick={handleDateSave}
              className="w-full px-4 py-2 bg-brand-primary text-white rounded-lg font-semibold hover:bg-brand-primary/90 transition-colors font-montserrat"
            >
              Guardar
            </button>
          </div>
        )}
      </div>

      {/* Slot */}
      <div className="relative">
        <button
          onClick={() => setSlotOpen(!slotOpen)}
          className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-brand-green"
          title="Cambiar horario"
        >
          <Clock className="w-5 h-5" />
        </button>
        {slotOpen && (
          <div className="absolute top-full right-0 mt-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 min-w-[140px]">
            {SLOT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleSlotChange(opt.value)}
                className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg font-montserrat"
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Location */}
      <div className="relative">
        <button
          onClick={() => setLocOpen(!locOpen)}
          className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-brand-primary"
          title="Cambiar ubicación"
        >
          <MapPin className="w-5 h-5" />
        </button>
        {locOpen && (
          <div className="absolute top-full right-0 mt-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 p-4 w-80">
            <p className="text-sm font-semibold text-white mb-2 font-montserrat">
              Lat, Lng (ej: -12.042104, -77.06698)
            </p>
            <input
              type="text"
              value={latLngStr}
              onChange={e => setLatLngStr(e.target.value)}
              placeholder="-12.042104, -77.06698"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded-lg outline-none focus:border-brand-primary mb-3 font-montserrat"
            />
            <button
              onClick={handleLocationSave}
              className="w-full px-4 py-2 bg-brand-primary text-white rounded-lg font-semibold hover:bg-brand-primary/90 transition-colors font-montserrat"
            >
              Guardar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatViewHelpers;